'use client';

import React, { useMemo, useState } from 'react';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';
import { PulseButton } from 'src/components';
import { countUndescribed, getRoleCapabilities } from './capabilities';
import { ROLE_SUMMARY_COPY } from './constants';

export interface RoleSummaryProps {
  /** A role's own permission codes (`action.resource.scope`). */
  permissions: string[];
  /**
   * Names the role in the toggle's accessible label, so a screen reader hears
   * which role is being expanded rather than one of five identical buttons.
   */
  roleLabel?: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * @description Plain-language snapshot of what a role grants, behind a "View
 *              details" disclosure. Collapsed, the roles list stays scannable;
 *              expanded, a role reads as a short list of capabilities ("Work
 *              their own territories", "Export table data"). Shown to License
 *              Managers in place of the permission matrix, which CS found
 *              unreadable (MKT-527).
 *
 *              The lines come from a curated catalog rather than from the codes
 *              themselves; see `capabilities.ts` for why, and for what is
 *              deliberately left undescribed.
 *
 *              Render this *outside* any `<label>` wrapping the role's checkbox
 *              — a button inside a label activates the label's control, so the
 *              toggle would assign the role as a side effect.
 * @returns React.ReactElement
 */
const RoleSummary: React.FC<RoleSummaryProps> = ({
  permissions,
  roleLabel,
  className,
  'data-testid': dataTestId = 'role-summary',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const capabilities = useMemo(
    () => getRoleCapabilities(permissions),
    [permissions]
  );
  const undescribedCount = useMemo(
    () => countUndescribed(permissions),
    [permissions]
  );

  const detailsId = `${dataTestId}-details`;
  const hasCapabilities = capabilities.length > 0 || undescribedCount > 0;

  return (
    <div
      className={twMerge('flex flex-col gap-1', className)}
      data-testid={dataTestId}
    >
      <PulseButton
        size="xs"
        variant="plain"
        color="primary"
        onClick={() => setIsExpanded((expanded) => !expanded)}
        endDecorator={isExpanded ? faChevronUp : faChevronDown}
        className="min-h-0 w-fit gap-1 px-0 py-0 text-xs font-medium"
        // No `aria-controls`: PulseButton declares a closed prop list and only
        // renders aria-label/-expanded/-haspopup/-pressed, so anything else is
        // dropped — silently, because TypeScript skips excess-property checks on
        // hyphenated JSX attributes. `aria-expanded` is the attribute the
        // disclosure pattern actually requires; `aria-controls` is optional and
        // poorly supported, so the details element keeps its `id` and we wire
        // the two together if PulseButton ever carries the prop.
        aria-expanded={isExpanded}
        aria-label={
          roleLabel
            ? `${
                isExpanded
                  ? ROLE_SUMMARY_COPY.hideDetails
                  : ROLE_SUMMARY_COPY.showDetails
              } for ${roleLabel}`
            : undefined
        }
        data-testid={`${dataTestId}-toggle`}
      >
        {isExpanded
          ? ROLE_SUMMARY_COPY.hideDetails
          : ROLE_SUMMARY_COPY.showDetails}
      </PulseButton>

      {isExpanded &&
        (hasCapabilities ? (
          <ul
            id={detailsId}
            className="flex list-disc flex-col gap-0.5 pl-4 text-xs text-neutral-600"
            data-testid={detailsId}
          >
            {capabilities.map((capability) => (
              <li
                key={capability.key}
                data-testid={`${dataTestId}-${capability.key}`}
              >
                {capability.label}
              </li>
            ))}
            {undescribedCount > 0 && (
              <li
                className="text-neutral-500"
                data-testid={`${dataTestId}-undescribed`}
              >
                {ROLE_SUMMARY_COPY.undescribed(undescribedCount)}
              </li>
            )}
          </ul>
        ) : (
          <p
            id={detailsId}
            className="text-xs text-neutral-500"
            data-testid={`${dataTestId}-empty`}
          >
            {ROLE_SUMMARY_COPY.empty}
          </p>
        ))}
    </div>
  );
};

export default RoleSummary;
